// Pure decisions for the /tg page (AGENTS.md section 7): what the component draws
// is decided here, and tgModel.test.ts proves it.
import type { TgDecision, TgFailure, TgOption } from './tgApi';

/**
 * The option drawn as the filled button. tg-team stores the recommendation as
 * free text, not as a key (migration, agent_decisions columns), so the match is
 * "the text starts with an option's label or key". No match: no filled button.
 */
export function recommendedKey(recommendation: string | null, options: TgOption[]): string | null {
  const text = (recommendation ?? '').trim().toLowerCase();
  if (text === '') return null;
  const candidates = options
    .flatMap((o) => [
      { key: o.key, word: o.label.trim().toLowerCase() },
      { key: o.key, word: o.key.toLowerCase() },
    ])
    .filter((c) => c.word !== '')
    .sort((a, b) => b.word.length - a.word.length);
  for (const c of candidates) {
    if (!text.startsWith(c.word)) continue;
    const next = text.charAt(c.word.length);
    if (next === '' || !/[a-z0-9]/.test(next)) return c.key;
  }
  return null;
}

export function ago(iso: string, now: number): string {
  const minutes = Math.max(0, (now - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${Math.round(minutes)} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} d ago`;
}

export const agentName = (agent: string) => agent.charAt(0).toUpperCase() + agent.slice(1);

export function answerLabel(d: TgDecision): string {
  return d.options.find((o) => o.key === d.answer)?.label ?? d.answer ?? '';
}

/** What the card says when an answer did not go through. null means the whole page changes instead. */
export function answerErrorText(code: TgFailure): string | null {
  switch (code) {
    case 'closed':
      return 'Already answered (probably in Telegram). Refreshing...';
    case 'stale':
      return 'This request changed. Refreshing before you answer.';
    case 'unknown_option':
      return 'That choice is no longer offered. Refreshing...';
    case 'not_found':
      return 'That decision is gone. Refreshing...';
    case 'rate_limited':
      return 'Too many answers just now. Wait a minute and try again.';
    case 'offline':
      return "Couldn't send. Check your connection and try again.";
    case 'expired':
      return null;
    default:
      return 'Something went wrong on our side. Try again.';
  }
}

/** Failures after which the page re-reads the list, because the list is what is wrong. */
export const refetchAfter = (code: TgFailure) =>
  code === 'closed' || code === 'stale' || code === 'unknown_option' || code === 'not_found';

// Step D chat. The server (tg_post_to_agent) is the judge; these only keep the box honest.
export const MAX_DRAFT = 2000;
const TRIM = /^[ \t\r\n]+|[ \t\r\n]+$/g;
export const trimDraft = (text: string) => text.replace(TRIM, '');
/** Characters as the database counts them (code points, after the same trim). */
export const draftCount = (text: string) => Array.from(trimDraft(text)).length;
export const canSend = (text: string) => {
  const n = draftCount(text);
  return n >= 1 && n <= MAX_DRAFT;
};

/** What the composer says when a send did not go through. null means the whole page changes instead. */
export function chatSendErrorText(code: TgFailure): string | null {
  switch (code) {
    case 'rate_limited':
      return 'Too many messages this hour (30 max). Try again later.';
    case 'offline':
      return "Couldn't send. Check your connection and try again.";
    case 'not_found':
      return 'This chat is not set up yet.';
    case 'unknown_option':
      return "That message can't be sent.";
    case 'expired':
      return null;
    default:
      return 'Something went wrong on our side. Try again.';
  }
}
